
import React, { useEffect, useMemo, useState, useRef } from "react";
import { fetchWheelState, spinWheel } from "./api/wheelApi";
import arrow from "../assets/arrow.svg";
import dollar from "../assets/dollar.png";
import sadsmile from "../assets/sad_smile.png";
import sunduk from "../assets/sunduk_1.png";
import "../assets/fonts/font-face.css";
import "../index.css";

const FALLBACK_PRIZE_IMAGES = [dollar, sadsmile, sunduk];


const SPIN_DURATION = 3500;


const REPEATS = 3;

function buildSlots(prizesFromApi) {
  
  const base =
    prizesFromApi && prizesFromApi.length
      ? prizesFromApi.map((p) => ({
          id: p.id,
          name: p.prize_name,
          img: p.prize_photo,
          isLose: !!p.is_lose,
        }))
      : FALLBACK_PRIZE_IMAGES.map((img, idx) => ({
          id: idx + 1,
          name: idx === 1 ? "nothing" : "prize",
          img,
          isLose: idx === 1,
        }));

  
  const res = [];
  for (let r = 0; r < REPEATS; r += 1) {
    for (let i = 0; i < base.length; i += 1) {
      res.push({ ...base[i] });
    }
  }

  return res;
}

export const WheelOfFortune = ({ initData }) => {
  const [status, setStatus] = useState("idle"); 
  const [result, setResult] = useState("none"); 
  const [prizeText, setPrizeText] = useState(null);
  const [prizeImage, setPrizeImage] = useState(null);
  const [remainingMs, setRemainingMs] = useState(0);
  const [rotation, setRotation] = useState(0);
  const [loading, setLoading] = useState(false);

  const [prizesFromApi, setPrizesFromApi] = useState([]);
  const [wheelStatus, setWheelStatus] = useState(true); 

  const spinTimeoutRef = useRef(null);

  
  const arrowRef = useRef(null);
  const cellRefs = useRef([]);

  const slots = useMemo(() => buildSlots(prizesFromApi), [prizesFromApi]);
  const SEGMENTS = slots.length || 1;
  const anglePer = 360 / SEGMENTS;

  
  const { iconRadius, iconAngleOffset } = useMemo(() => {
    if (SEGMENTS <= 3) {
      return { iconRadius: 32, iconAngleOffset: 0 };
    }
    if (SEGMENTS <= 6) {
      
      return { iconRadius: 34, iconAngleOffset: 0 };
    }
    if (SEGMENTS <= 9) {
      return { iconRadius: 35, iconAngleOffset: 0 };
    }
    
    return { iconRadius: 36, iconAngleOffset: 0 };
  }, [SEGMENTS]);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        setLoading(true);
        console.log("[WheelOfFortune] initData =", initData);

        const data = await fetchWheelState(initData);

        const wheelPrizes = data.prizes || [];
        if (!isMounted) return;

        setPrizesFromApi(wheelPrizes);

        const remaining = data.remaining_ms || 0;
        setRemainingMs(remaining);

        const enabled = !!data.status;
        setWheelStatus(enabled);

        if (!enabled) {
          setStatus("offline");
        } else if (remaining > 0) {
          setStatus("cooldown");
        } else {
          setStatus("idle");
        }
      } catch (e) {
        console.error("Failed to fetch wheel state", e);
        if (!isMounted) return;
        setStatus("offline");
        setWheelStatus(false);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (initData) {
      load();
    } else {
      setStatus("offline");
      setWheelStatus(false);
    }

    return () => {
      isMounted = false;
      if (spinTimeoutRef.current) {
        clearTimeout(spinTimeoutRef.current);
      }
    };
  }, [initData]);

  useEffect(() => {
    if (status !== "cooldown") return;
    if (remainingMs <= 0) {
      setStatus("idle");
      setRemainingMs(0);
      return;
    }

    const id = setInterval(() => {
      setRemainingMs((prev) => {
        const next = prev - 1000;
        if (next <= 0) {
          clearInterval(id);
          setStatus("idle");
          return 0;
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(id);
  }, [status, remainingMs]);

  const formattedCooldown = useMemo(() => {
    const totalSec = Math.max(0, Math.floor(remainingMs / 1000));
    const days = Math.floor(totalSec / (24 * 3600));
    const hours = Math.floor((totalSec % (24 * 3600)) / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    const seconds = totalSec % 60;

    if (days > 0) {
      const dd = String(days).padStart(2, "0");
      const hh = String(hours).padStart(2, "0");
      const mm = String(minutes).padStart(2, "0");
      const ss = String(seconds).padStart(2, "0");
      return `${dd}:${hh}:${mm}:${ss}`;
    } else {
      const hh = String(hours).padStart(2, "0");
      const mm = String(minutes).padStart(2, "0");
      const ss = String(seconds).padStart(2, "0");
      return hours > 0 ? `${hh}:${mm}:${ss}` : `${mm}:${ss}`;
    }
  }, [remainingMs]);

  const handleSpin = async () => {
  // если уже знаем, что колесо оффлайн — даже не пытаемся
  if (!wheelStatus) {
    setStatus("offline");
    setWheelStatus(false);
    return;
  }

  if (loading || status !== "idle") return;
  if (!initData) return;

  try {
    
    const freshState = await fetchWheelState(initData);
    console.log(freshState);
    if (!freshState.status) {
      // колесо уже выключили на бэке
      setWheelStatus(false);
      setStatus("offline");
      // при желании можно ещё обновить призы/таймеры
      setPrizesFromApi(freshState.prizes || []);
      setRemainingMs(freshState.remaining_ms || 0);
      return;
    }

    // если всё ок — дальше логика спина как было
    if (spinTimeoutRef.current) {
      clearTimeout(spinTimeoutRef.current);
    }

    setLoading(true);
    setResult("none");

    const data = await spinWheel(initData);

    const winningId = data.prize_id;
    const prizeName = data.prize_name;
    const prizePhoto = data.prize_photo || null;

    if (data.next_spin_at) {
      const now = Date.now();
      const next = new Date(data.next_spin_at).getTime();
      const diff = next - now;
      if (diff > 0) {
        setRemainingMs(diff);
        setStatus("cooldown");
      } else {
        setRemainingMs(0);
        setStatus("idle");
      }
    } else {
      setRemainingMs(0);
      setStatus("idle");
    }

    let candidates: number[] = [];
    if (winningId != null) {
      slots.forEach((slot, idx) => {
        if (slot.id === winningId) candidates.push(idx);
      });
    }

    if (candidates.length === 0) {
      slots.forEach((slot, idx) => {
        if (
          (prizeName && slot.name === prizeName) ||
          (prizePhoto && slot.img === prizePhoto)
        ) {
          candidates.push(idx);
        }
      });
    }

    const localSegments = slots.length || 1;
    const localAnglePer = 360 / localSegments;

    const targetIndex =
      candidates.length > 0
        ? candidates[Math.floor(Math.random() * candidates.length)]
        : Math.floor(Math.random() * localSegments);

    const extraSpins = 4;
    const POINTER_ANGLE = 270;

    const baseCenterAngle =
      90 + localAnglePer * targetIndex + localAnglePer / 2 + iconAngleOffset;

    const prevRotation = rotation;

    let rawDelta =
      POINTER_ANGLE - baseCenterAngle - (prevRotation % 360);

    rawDelta = ((rawDelta % 360) + 360) % 360;

    const deltaAngle = extraSpins * 360 + rawDelta;

    setRotation((prev) => prev + deltaAngle);

    spinTimeoutRef.current = setTimeout(() => {
      let visualIndex = targetIndex;

      try {
        const arrowEl = arrowRef.current;
        const cells = cellRefs.current || [];

        if (arrowEl && cells.length) {
          const arrowRect = arrowEl.getBoundingClientRect();
          const arrowX = arrowRect.left + arrowRect.width / 2;
          const arrowY = arrowRect.top + arrowRect.height / 2;

          let bestIdx = targetIndex;
          let bestDist = Infinity;

          cells.forEach((el, idx) => {
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const cx = rect.left + rect.width / 2;
            const cy = rect.top + rect.height / 2;
            const dx = cx - arrowX;
            const dy = cy - arrowY;
            const dist = dx * dx + dy * dy;
            if (dist < bestDist) {
              bestDist = dist;
              bestIdx = idx;
            }
          });

          visualIndex = bestIdx;
        }
      } catch (err) {
        console.warn("Visual detection error, fallback", err);
      }

      const targetSlot = slots[visualIndex];
      const isLose = targetSlot?.isLose ?? false;

      setPrizeText(targetSlot?.name || prizeName || null);
      setPrizeImage(targetSlot?.img || prizePhoto || null);
      setResult(isLose ? "lose" : "win");

      setLoading(false);
    }, SPIN_DURATION);
  } catch (e) {
    console.error("Failed to spin wheel", e);
    setLoading(false);
    alert("Ошибка сервера. Попробуйте позже.");
  }
};



  const handleCloseResult = () => {
    setResult("none");
  };

  const isButtonDisabled =
    loading || status === "cooldown" || status === "offline";

  return (
    <section className="main_section">
      <h1>
        Колесо <br /> Фортуны
      </h1>

      {!wheelStatus ? (
        <div className="wheel_unavailable">
          <div className="wheel_unavailable_title">
            Рулетка временно не работает, следите за обновлениями в нашем
            телеграмм канале
          </div>
          <a
            href="https://t.me/wizardrezervv"
            target="_blank"
            rel="noreferrer"
            className="spin_button not_avalible"
          >
            ССЫЛКА
          </a>
        </div>
      ) : (
        <>
          {/* колесо */}
          <div className="wheel_container">
            <img ref={arrowRef} src={arrow} alt="" className="arrow" />
            <div className="circle_center" />
            <div
              className="circle_dynamic"
              style={{
                transform: `translate(-50%,-50%) rotate(${rotation}deg)`,
              }}
            >
              {Array.from({ length: SEGMENTS }).map((_, i) => {
                return (
                  <div
                    key={`divider-${i}`}
                    className="sector_line"
                    style={{
                      transform: `rotate(${anglePer * i}deg)`
                    }}
                  />
                );
              })}

              {/* иконки в секторах */}
              {slots.map((slot, i) => {
                
                const centerAngleDeg =
                  90 + anglePer * i + anglePer / 2 + iconAngleOffset;
                const centerAngleRad = (centerAngleDeg * Math.PI) / 180;

                const x = 50 + iconRadius * Math.cos(centerAngleRad);
                const y = 50 + iconRadius * Math.sin(centerAngleRad);

                return (
                  <div
                    key={`${slot.id}-${i}`}
                    className="wheel_cell"
                    data-index={i}
                    ref={(el) => {
                      cellRefs.current[i] = el;
                    }}
                    style={{
                      left: `${x}%`,
                      top: `${y}%`,
                      transform: `translate(-50%, -50%) rotate(${centerAngleDeg + 90
                        }deg)`,
                    }}
                  >
                    <img
                      src={slot.img}
                      alt={slot.name || ""}
                      className="wheel_cell_img"
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <button
            onClick={handleSpin}
            disabled={isButtonDisabled}
            className="spin_button"
          >
            {status === "cooldown"
              ? formattedCooldown
              : status === "offline"
                ? "Недоступно"
                : loading
                  ? "Spining..."
                  : "Spin"}
          </button>

          {/* модалка результата */}
          {result !== "none" && (
            <div className="result_overlay">
              <div
                className={`result_modal ${result === "win"
                  ? "result_modal--win"
                  : "result_modal--lose"
                  }`}
              >
                {result === "win" ? (
                  <>
                    <div className="result_title">
                      ПОЗДРАВЛЯЕМ! <br /> ВОТ ВАШ ПРИЗ
                    </div>
                    <div className="result_icon result_icon--win">
                      <img src={prizeImage || dollar} alt="Prize" />
                    </div>
                    <div className="result_amount">
                      {prizeText || "Приз"}
                    </div>

                    <a
                      href="https://t.me/PodWizard"
                      target="_blank"
                      rel="noreferrer"
                      className="result_btn result_btn--primary"
                      onClick={handleCloseResult}
                    >
                      Забрать
                    </a>
                  </>
                ) : (
                  <>
                    <div className="result_title">
                      ПОВЕЗЁТ В СЛЕДУЮЩИЙ РАЗ
                    </div>
                    <div className="result_icon">
                      <img
                        src={prizeImage || sadsmile}
                        alt={prizeText || "Nothing"}
                      />
                    </div>

                    <button
                      className="result_btn result_btn--secondary again"
                      onClick={handleCloseResult}
                    >
                      ПОПРОБОВАТЬ
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default WheelOfFortune;
